import React, { useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { toast } from "react-toastify";
import { usePatchCvMutation } from "../../../lib/cvsSlice";
import { useCvStepNavigation } from "../../../contexts/CvStepNavigationContext";
import { primaryRoles, careerBreakType } from "config";

/* ─── helpers ─────────────────────────────────────────────────────── */

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

const getMonthName  = (date) => (date instanceof Date && !isNaN(date) ? MONTH_NAMES[date.getMonth()] : "");
const getYear       = (date) => (date instanceof Date && !isNaN(date) ? String(date.getFullYear()) : "");
const parseMonthYear = (month, year) => {
  if (!month || !year) return null;
  const d = new Date(`${month} 1, ${year}`);
  return isNaN(d) ? null : d;
};

// "June" → "06"
const monthToNum = (month) => String(MONTH_NAMES.indexOf(month) + 1).padStart(2, "0");

// "June", "2020" → "06/2020"
const fmtMonthYear = (month, year) =>
  month && year ? `${monthToNum(month)}/${year}` : "";

// experience/break → "06/2020 - Present"
const fmtRange = (startMonth, startYear, endMonth, endYear) => {
  const start = fmtMonthYear(startMonth, startYear);
  const end   = endMonth && endYear ? fmtMonthYear(endMonth, endYear) : "Present";
  return start ? `${start} - ${end}` : "";
};

const SELECT_OPTIONS = ["Add New Experience", "Add Career Break"];

const EMPTY_FORM = {
  selectedOption: "Add New Experience",
  startDate:   null,
  endDate:     null,
  onGoing:     true,
  companyName: "",
  role:        "",
  breakType:   "",
  location:    "",
  description: "",
};

/* ─── ExperienceCard ──────────────────────────────────────────────── */

const ExperienceCard = ({ label, sub, onEdit, onDelete, included, onToggle }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      backgroundColor: "#F4F2F1",
      borderRadius: "100px",
      padding: "10px 20px",
    }}
  >
    {/* Left: title + subtitle */}
    <Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "14px",
          fontFamily: "Inter, sans-serif",
          color: "#1A1A1A",
          lineHeight: "20px",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "11px",
          fontFamily: "Inter, sans-serif",
          color: "#7A706F",
          lineHeight: "16px",
          textTransform: "uppercase",
          letterSpacing: "0.3px",
        }}
      >
        {sub}
      </Typography>
    </Box>

    {/* Right: actions */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <IconButton size="small" onClick={onEdit} sx={{ color: "#483C3B" }}>
        <EditIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <IconButton size="small" onClick={onDelete} sx={{ color: "#483C3B" }}>
        <HighlightOffIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 0.5 }}>
        <Switch
          size="small"
          checked={included}
          onChange={(e) => onToggle(e.target.checked)}
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": { color: "#fff" },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#4CAF50" },
          }}
        />
        <Typography sx={{ fontSize: "11px", fontFamily: "Inter, sans-serif", color: "#3D3634", whiteSpace: "nowrap" }}>
          Include in CV
        </Typography>
      </Box>
    </Box>
  </Box>
);

/* ─── main component ──────────────────────────────────────────────── */

const WorkExperiences = ({ experiences = [], careerBreak = [], cvId }) => {
  const [showForm, setShowForm]   = useState(experiences.length === 0 && careerBreak.length === 0);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);

  const [patchCv] = usePatchCvMutation();
  const { goToNextStep, goToPrevStep } = useCvStepNavigation();

  const hasItems = experiences.length > 0 || careerBreak.length > 0;

  /* ── helpers ── */
  const setField = (key) => (valueOrEvent) => {
    const value =
      valueOrEvent && valueOrEvent.target !== undefined
        ? valueOrEvent.target.value
        : valueOrEvent;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const openAddForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const openEditForm = (exp) => {
    setForm({
      selectedOption: "Add New Experience",
      startDate:   parseMonthYear(exp.startMonth, exp.startYear),
      endDate:     parseMonthYear(exp.endMonth,   exp.endYear),
      onGoing:     !exp.endMonth || !exp.endYear,
      companyName: exp.companyName || "",
      role:        exp.role        || "",
      breakType:   "",
      location:    exp.location    || "",
      description: exp.description || "",
    });
    setEditingId(exp._id);
    setShowForm(true);
  };

  const openEditBreakForm = (cb) => {
    setForm({
      selectedOption: "Add Career Break",
      startDate:   parseMonthYear(cb.startMonth, cb.startYear),
      endDate:     parseMonthYear(cb.endMonth,   cb.endYear),
      onGoing:     !cb.endMonth || !cb.endYear,
      companyName: "",
      role:        "",
      breakType:   cb.type        || "",
      location:    cb.location    || "",
      description: cb.description || "",
    });
    setEditingId(cb._id);
    setShowForm(true);
  };

  const cancelForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    if (hasItems) setShowForm(false);
  };

  /* ── save ── */
  const handleSave = async () => {
    const isBreak = form.selectedOption === "Add Career Break";
    if (!form.startDate) return toast.error("Start date is required");
    if (!form.onGoing && !form.endDate) return toast.error("End date is required, or mark as Ongoing");
    if (isBreak) {
      if (!form.breakType)   return toast.error("Break type is required");
      if (!form.location)    return toast.error("Location is required");
      if (!form.description) return toast.error("Description is required");
    } else {
      if (!form.companyName) return toast.error("Company name is required");
      if (!form.role)        return toast.error("Role is required");
    }

    setSaving(true);
    try {
      const startMonth = getMonthName(form.startDate);
      const startYear  = getYear(form.startDate);
      const endMonth   = form.onGoing ? "" : getMonthName(form.endDate);
      const endYear    = form.onGoing ? "" : getYear(form.endDate);

      if (isBreak) {
        const breakPayload = { startMonth, startYear, endMonth, endYear, onGoing: form.onGoing, type: form.breakType, location: form.location, description: form.description };
        if (editingId) {
          await patchCv({ cvId, careerBreak: [{ id: editingId, ...breakPayload }] }).unwrap();
        } else {
          await patchCv({ cvId, careerBreak: [breakPayload] }).unwrap();
        }
      } else {
        const payload = { startMonth, startYear, endMonth, endYear, companyName: form.companyName, role: form.role, location: form.location, description: form.description };
        if (editingId) {
          await patchCv({ cvId, experiences: [{ id: editingId, ...payload }] }).unwrap();
        } else {
          await patchCv({ cvId, experiences: [payload] }).unwrap();
        }
      }

      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(`Failed to save: ${err?.data?.message ?? err.message}`);
    } finally {
      setSaving(false);
    }
  };

  /* ── delete ── */
  const handleDeleteExp = async (id) => {
    try {
      await patchCv({ cvId, experiences: [{ id, delete: true }] }).unwrap();
      toast.info("Experience removed");
    } catch {
      toast.error("Failed to remove experience");
    }
  };

  const handleDeleteBreak = async (id) => {
    try {
      await patchCv({ cvId, careerBreak: [{ id, delete: true }] }).unwrap();
      toast.info("Career break removed");
    } catch {
      toast.error("Failed to remove career break");
    }
  };

  /* ── include in CV toggle ── */
  const handleToggleExp = async (exp, included) => {
    try {
      await patchCv({ cvId, experiences: [{ id: exp._id, excludeInCv: !included }] }).unwrap();
    } catch {
      toast.error("Failed to update");
    }
  };

  const isBreakForm = form.selectedOption === "Add Career Break";

  /* ─── render ────────────────────────────────────────────────────── */
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {showForm ? (
          /* ══ Form view ══════════════════════════════════════════════ */
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxWidth: "75%" }}>

            <TextField select label="Select Option" value={form.selectedOption} onChange={setField("selectedOption")} fullWidth size="small">
              {SELECT_OPTIONS.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
            </TextField>

            <DatePicker
              label="Start Date"
              inputFormat="dd-MM-yyyy"
              value={form.startDate}
              onChange={setField("startDate")}
              renderInput={(params) => <TextField {...params} fullWidth size="small" InputLabelProps={{ shrink: true }} />}
            />

            <DatePicker
              label="End Date"
              inputFormat="dd-MM-yyyy"
              value={form.endDate}
              onChange={setField("endDate")}
              disabled={form.onGoing}
              renderInput={(params) => (
                <TextField {...params} fullWidth size="small" InputLabelProps={{ shrink: true }} sx={form.onGoing ? { opacity: 0.5 } : {}} />
              )}
            />

            <FormControlLabel
              sx={{ my: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={form.onGoing}
                  onChange={(e) => setForm((f) => ({ ...f, onGoing: e.target.checked, endDate: e.target.checked ? null : f.endDate }))}
                  sx={{ color: "#E84610", "&.Mui-checked": { color: "#E84610" } }}
                />
              }
              label={<Typography sx={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: "14px" }}>Ongoing</Typography>}
            />

            {!isBreakForm && (
              <>
                <TextField label="Company Name" placeholder="Enter Name" value={form.companyName} onChange={setField("companyName")} fullWidth size="small" />
                <Autocomplete
                  options={primaryRoles}
                  value={form.role || null}
                  onChange={(_, v) => setForm((f) => ({ ...f, role: v || "" }))}
                  renderInput={(params) => <TextField {...params} label="Role" placeholder="Search Role" fullWidth size="small" />}
                  freeSolo
                />
              </>
            )}

            {isBreakForm && (
              <>
                <TextField select label="Break Type" value={form.breakType} onChange={setField("breakType")} fullWidth size="small">
                  {careerBreakType.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
                <TextField label="Location" placeholder="e.g. London, United Kingdom" value={form.location} onChange={setField("location")} fullWidth size="small" />
                <TextField label="Description" placeholder="Describe your career break" value={form.description} onChange={setField("description")} fullWidth size="small" multiline rows={2} />
              </>
            )}

            <Box>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{
                  backgroundColor: "#483C3B",
                  "&:hover": { backgroundColor: "#332B2A" },
                  "&:disabled": { backgroundColor: "#9E9E9E" },
                  borderRadius: "6px",
                  px: 3,
                  fontWeight: 700,
                  letterSpacing: "0.8px",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                {saving ? "Saving..." : "SAVE"}
              </Button>
            </Box>
          </Box>

        ) : (
          /* ══ List view ══════════════════════════════════════════════ */
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>

            {/* Experience cards */}
            {experiences.map((exp) => (
              <ExperienceCard
                key={exp._id}
                label={exp.role}
                sub={`${exp.companyName ? exp.companyName + ", " : ""}${fmtRange(exp.startMonth, exp.startYear, exp.endMonth, exp.endYear)}`}
                onEdit={() => openEditForm(exp)}
                onDelete={() => handleDeleteExp(exp._id)}
                included={!exp.excludeInCv}
                onToggle={(included) => handleToggleExp(exp, included)}
              />
            ))}

            {/* Career break cards */}
            {careerBreak.map((cb) => (
              <ExperienceCard
                key={cb._id}
                label={cb.type}
                sub={fmtRange(cb.startMonth, cb.startYear, cb.endMonth, cb.endYear)}
                onEdit={() => openEditBreakForm(cb)}
                onDelete={() => handleDeleteBreak(cb._id)}
                included={!cb.excludeInCv}
                onToggle={() => {}}
              />
            ))}

            {/* Add another */}
            <Button
              onClick={openAddForm}
              disableRipple
              disableFocusRipple
              startIcon={<AddIcon sx={{ fontSize: "1rem" }} />}
              sx={{
                color: "#1A1A1A",
                textTransform: "uppercase",
                textDecoration: "underline",
                fontFamily: "Inter, sans-serif",
                fontWeight: 600,
                fontSize: "0.8125rem",
                letterSpacing: "0.5px",
                padding: 0,
                minWidth: "unset",
                alignSelf: "flex-start",
                mt: 0.5,
              }}
            >
              Add Another Experience
            </Button>
          </Box>
        )}

        {/* ── BACK / NEXT ─────────────────────────────────────────── */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, width: "75%" }}>
          <Button
            variant="outlined"
            onClick={showForm && hasItems ? cancelForm : goToPrevStep}
            sx={{
              width: 84, height: 42,
              borderRadius: "8px",
              borderColor: "currentColor",
              textTransform: "none",
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
            }}
          >
            BACK
          </Button>

          <Button
            variant="contained"
            onClick={goToNextStep}
            sx={{
              width: 84, height: 42,
              borderRadius: "8px",
              backgroundColor: "#E84610",
              "&:hover": { backgroundColor: "#C73A0D" },
              textTransform: "none",
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              boxShadow: "none",
            }}
          >
            NEXT
          </Button>
        </Box>

      </Box>
    </LocalizationProvider>
  );
};

export default WorkExperiences;
